import React from 'react';

export function OptumBackfillFields({
  loginId,
  fromDate,
  toDate,
  onLoginIdChange,
  onFromDateChange,
  onToDateChange,
  disabled,
}) {
  const latestDate = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  return <>
    <p className="text-sm text-gray-600">
      Optum uses the exact Confirmed credential stored for this site and login ID. Choose inclusive
      Payment Dates for up to 31 days, ending before today (UTC). Credentials and MFA are resolved
      by the worker and are never sent from this form.
    </p>
    <div>
      <label htmlFor="optum-login-id" className="block text-sm font-medium text-gray-700 mb-2">
        Stored login ID *
      </label>
      <input id="optum-login-id" type="text" inputMode="numeric" value={loginId} required
        disabled={disabled} onChange={event => onLoginIdChange(event.target.value)}
        placeholder="Enter website login ID..."
        className="w-full p-2 border border-gray-300 rounded-md" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label htmlFor="optum-from-date" className="block text-sm font-medium text-gray-700 mb-2">
          Payment from date *
        </label>
        <input id="optum-from-date" type="date" value={fromDate} max={latestDate} required
          disabled={disabled} onChange={event => onFromDateChange(event.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md" />
      </div>
      <div>
        <label htmlFor="optum-to-date" className="block text-sm font-medium text-gray-700 mb-2">
          Payment to date *
        </label>
        <input id="optum-to-date" type="date" value={toDate} min={fromDate || undefined}
          max={latestDate} required disabled={disabled}
          onChange={event => onToDateChange(event.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md" />
      </div>
    </div>
  </>;
}

export function OptumBackfillResult({ job }) {
  const result = job.result || {};
  const complete = job.status === 'COMPLETED'
    && result.coverage_complete === true
    && !result.ingestion_outcome_unknown;
  const stage = {
    validating: 'Checking the stored credential and site routing',
    waiting_for_optum_session: 'Waiting for the Optum session',
    enumerating: 'Reading Payment Summary pages',
    downloading: 'Downloading an 835',
    ingesting: 'Submitting an 835',
    finished: 'Finished',
    failed: 'Stopped',
  }[result.stage] || 'Waiting';
  const outcomeText = {
    no_data: 'No 835 files matched the requested Payment Dates.',
    duplicate_only: 'All matching files were already recorded for this site.',
  }[result.outcome];
  return <div className="text-sm mt-2 text-gray-700">
    {result.scope && <p>Payment Dates: {result.scope.from_date}–{result.scope.to_date}</p>}
    <p>{complete ? 'Finished' : stage} · {result.pages_visited || 0} pages · {result.links_seen || 0} links · {result.links_in_scope || 0} in range</p>
    <p>{result.files_submitted || 0} submitted · {result.files_duplicate || 0} duplicates · {result.overlap_duplicates || 0} overlap duplicates</p>
    {complete && <p>Collection complete. This confirms portal coverage and submission outcomes; it does not confirm downstream posting.</p>}
    {complete && outcomeText && <p>{outcomeText}</p>}
    {['FAILED', 'CANCELLED'].includes(job.status) && <p>Collection is incomplete. Files with verified submission outcomes are retained.</p>}
    {job.status === 'COMPLETED' && !complete && <p className="text-red-700">Collection completeness could not be confirmed. Review this job before retrying.</p>}
    {job.error_message && <p className="text-red-700">{job.error_message}</p>}
    {result.ingestion_outcome_unknown && <p className="text-red-700">A file's ingestion outcome is uncertain. Reconcile its processing task and file record before retrying.</p>}
  </div>;
}
